import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCredits } from '../../hooks/useCredits';
import { creditService } from '../../services/creditService';
import { subscriptionService } from '../../services/subscriptionService';
import { CREDIT_COSTS } from '../../config/creditCosts';
import logo from '../../assets/new-logo.png';
import { toast } from 'sonner';

export default function CreditsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    balance,
    isLoadingBalance,
    isErrorBalance,
    topupOptions,
    isLoadingTopupOptions,
    isErrorTopupOptions,
    modulePricing,
    invalidateCredits
  } = useCredits();

  const websiteBuilderCost = modulePricing.find(p => p.module_code === 'WEBSITE_BUILDER')?.credit_cost ?? CREDIT_COSTS.FULL_WEBSITE;

  const [customAmount, setCustomAmount] = useState<number>(500);
  const [purchasingId, setPurchasingId] = useState<number | string | null>(null);

  // Invalidate balance on mount to fetch the latest post-purchase balance
  useEffect(() => {
    invalidateCredits();
    
    // Check if coming back from a success redirect
    const params = new URLSearchParams(location.search);
    if (params.get('payment') === 'success') {
      toast.success('Payment completed! Your credits balance has been updated.');
      // Session-scoped stopgap for the notification-bell top-up dot (no read/unread
      // notification-center infra exists yet) — cleared when the bell is opened.
      sessionStorage.setItem('topup_success_pending', '1');
    } else if (params.get('payment') === 'failure') {
      toast.error('Payment failed. Please try again.');
    }
  }, [location.search]);

  // Fetch active subscription details
  const { data: activeSub, isLoading: isLoadingSub } = useQuery({
    queryKey: ['subscriptions', 'current'],
    queryFn: () => subscriptionService.getCurrentSubscription(),
    retry: false,
  });

  // Extract topup packages (fixed tiers)
  const packages = topupOptions
    .filter(opt => !opt.is_manual_entry)
    .sort((a, b) => (a.tier_amount || 0) - (b.tier_amount || 0));

  // Extract manual entry tiers for custom amount stepper rate calculations
  const manualTiers = topupOptions
    .filter(opt => opt.is_manual_entry)
    .sort((a, b) => (b.min_threshold || 0) - (a.min_threshold || 0));

  // Derive custom amount rate
  const getCustomRate = (amount: number) => {
    if (manualTiers.length === 0) {
      // Fallback to defaults if options are still loading
      return amount >= 500 ? 0.90 : 1.00;
    }
    const applicableTier = manualTiers.find(tier => amount >= (tier.min_threshold || 0));
    return applicableTier ? applicableTier.rate_per_credit : 1.00;
  };

  const getCustomPrice = (amount: number) => {
    return amount * getCustomRate(amount);
  };

  // Best rate card derivation
  const bestRateOpt = topupOptions.find(opt => opt.is_manual_entry && opt.min_threshold === 500);
  const bestRate = bestRateOpt ? bestRateOpt.rate_per_credit : 0.90;

  const handleBuy = async (tierId: number | null, manualAmount: number | null, keyId: number | string) => {
    try {
      setPurchasingId(keyId);
      const res = await creditService.purchaseCredits({
        tier_id: tierId,
        manual_amount: manualAmount,
        payment_method: 'UPI',
      });

      const formFields = res.checkout_type === "merchant_hosted"
        ? res.checkout_payload
        : res.params;

      if (!res.action_url || !formFields) {
        toast.error("Failed to initiate payment form.");
        return;
      }

      // Reusing standard payment form submission
      const form = document.createElement("form");
      form.method = "POST";
      form.action = res.action_url;

      Object.entries(formFields).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = String(value);
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "An error occurred initiating checkout.");
    } finally {
      setPurchasingId(null);
    }
  };

  // Helper to highlight "Most Popular" card (middle tier of 3 tiers)
  const isFeaturedTier = (index: number, tierAmount: number | null) => {
    if (packages.length === 3) {
      return index === 1; // Middle tier
    }
    return tierAmount === 500; // Default fallback
  };

  const currentBalance = balance !== undefined && balance !== null ? balance.total_credits_balance : 0;
  const expiryDateString = balance?.expiry_date 
    ? new Date(balance.expiry_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : 'No Active Subscription';

  return (
    <div className="min-h-screen bg-slate-50/50 flex flex-col font-sans">
      {/* Header Bar */}
      <header className="bg-white border-b border-slate-100 px-8 py-4 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 flex items-center justify-center hover:bg-slate-100 transition-all cursor-pointer"
            aria-label="Back"
          >
            <i className="ri-arrow-left-line text-lg"></i>
          </button>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <img
              src={logo}
              alt="4Sight Logo"
              className="h-8 w-auto object-contain flex-shrink-0"
            />
            <span className="text-slate-300">/</span>
            <span className="text-slate-600 font-semibold">Credits Manager</span>
          </div>
        </div>

        <button
          onClick={() => navigate('/marketing-dashboard')}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 transition-all cursor-pointer"
        >
          <i className="ri-home-line"></i>
          Home
        </button>
      </header>

      <main className="flex-1 overflow-y-auto px-8 py-10 max-w-6xl mx-auto w-full">
        {/* Hero title */}
        <div className="mb-10 text-left">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 border border-blue-200 text-xs font-semibold text-blue-700 mb-3 uppercase tracking-wider">
            <i className="ri-database-2-line"></i>
            Buy Credits
          </span>
          <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Top Up Your Credits</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-xl">
            Increase your credits balance to keep generating videos, carousels, SEO articles, and websites without interruptions.
          </p>
        </div>

        {/* Section 1: Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          {/* Card 1: Balance */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between h-[150px]">
            <div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Current Balance</span>
              {isLoadingBalance ? (
                <div className="h-8 w-24 bg-slate-100 rounded animate-pulse mt-2" />
              ) : (
                <h3 className="text-3xl font-black text-slate-800">{currentBalance.toLocaleString()}</h3>
              )}
            </div>
            <span className="text-xs text-slate-500 flex items-center gap-1 mt-3">
              <i className="ri-calendar-line"></i>
              Expiry: {isLoadingBalance ? '...' : expiryDateString}
            </span>
          </div>

          {/* Card 2: Best Rate */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between h-[150px]">
            <div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Best Rate</span>
              {isLoadingTopupOptions ? (
                <div className="h-8 w-24 bg-slate-100 rounded animate-pulse mt-2" />
              ) : (
                <h3 className="text-3xl font-black text-emerald-600">₹{bestRate.toFixed(2)}</h3>
              )}
            </div>
            <span className="text-xs text-slate-500 flex items-center gap-1 mt-3">
              <i className="ri-percent-line"></i>
              Applicable on purchases of 500+ credits
            </span>
          </div>

          {/* Card 3: Active Subscription */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col justify-between h-[150px]">
            <div>
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Active Plan</span>
              {isLoadingSub ? (
                <div className="h-8 w-32 bg-slate-100 rounded animate-pulse mt-2" />
              ) : (
                <h3 className="text-2xl font-black text-blue-600 truncate">
                  {activeSub ? activeSub.plan_name : 'Free Tier'}
                </h3>
              )}
            </div>
            <span className="text-xs text-slate-500 flex items-center gap-1 mt-3">
              <i className="ri-shield-check-line"></i>
              {activeSub ? `Status: ${activeSub.status.toUpperCase()}` : 'Upgrade plan for bonus benefits'}
            </span>
          </div>
        </div>

        {/* Section 2: Top-up Tiers */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-slate-800 mb-6">Select a Package</h2>

          {isLoadingTopupOptions ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white p-6 rounded-2xl border border-slate-200 h-[220px] animate-pulse flex flex-col justify-between">
                  <div className="h-4 bg-slate-100 rounded w-1/3 mb-2" />
                  <div className="h-8 bg-slate-100 rounded w-1/2" />
                  <div className="h-10 bg-slate-100 rounded w-full mt-4" />
                </div>
              ))}
            </div>
          ) : isErrorTopupOptions ? (
            <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-2xl text-center">
              <i className="ri-error-warning-line text-3xl block mb-2"></i>
              <p className="font-semibold">Failed to load packages</p>
              <button
                onClick={() => invalidateCredits()}
                className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {packages.map((tier, index) => {
                const isPopular = isFeaturedTier(index, tier.tier_amount);
                const buttonLoading = purchasingId === tier.id;

                return (
                  <div
                    key={tier.id}
                    className={`bg-white rounded-2xl border p-6 flex flex-col justify-between min-h-[240px] relative transition-all duration-300 ${
                      isPopular
                        ? 'border-[#00ADC4] ring-1 ring-[#00ADC4] shadow-md shadow-cyan-50'
                        : 'border-slate-200 shadow-sm hover:shadow-md'
                    }`}
                  >
                    {isPopular && (
                      <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#00ADC4] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                        Most Popular
                      </span>
                    )}

                    <div className="text-left">
                      <span className="text-sm font-bold text-slate-400 block mb-1">TOP UP</span>
                      <h3 className="text-2xl font-black text-slate-800 mb-2">
                        {tier.tier_amount?.toLocaleString()} Credits
                      </h3>
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-3xl font-black text-slate-800">
                          ₹{tier.price?.toFixed(0)}
                        </span>
                        <span className="text-xs text-slate-400 font-medium">inclusive of taxes</span>
                      </div>
                      <span className="inline-flex items-center text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded">
                        ₹{tier.rate_per_credit.toFixed(2)} / credit
                      </span>
                    </div>

                    <button
                      onClick={() => handleBuy(tier.id, null, tier.id)}
                      disabled={purchasingId !== null}
                      className={`w-full mt-6 py-3 px-4 rounded-xl text-sm font-bold tracking-wide uppercase transition-all duration-300 cursor-pointer flex items-center justify-center gap-2 ${
                        isPopular
                          ? 'bg-[#00ADC4] hover:bg-[#00ADC4]/90 text-white shadow-sm'
                          : 'bg-slate-800 hover:bg-slate-900 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {buttonLoading ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          Processing...
                        </>
                      ) : (
                        <>
                          <i className="ri-secure-payment-line"></i>
                          Buy Package
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Section 3: Custom Stepper */}
        <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm mb-12 max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-slate-800 mb-2 text-left">Custom Top Up</h2>
          <p className="text-xs text-slate-500 text-left mb-6">
            Enter a custom credits amount. Price and per-credit rate adjust automatically based on volume.
          </p>

          <div className="flex flex-col md:flex-row gap-8 items-center">
            {/* Input & Stepper */}
            <div className="w-full md:w-1/2">
              <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden shadow-sm h-14">
                <button
                  type="button"
                  onClick={() => setCustomAmount(prev => Math.max(25, prev - 50))}
                  className="w-14 h-full bg-slate-50 hover:bg-slate-100 border-r border-slate-200 flex items-center justify-center text-slate-600 transition-colors cursor-pointer"
                >
                  <i className="ri-subtract-line text-lg"></i>
                </button>
                <input
                  type="number"
                  min="25"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(parseInt(e.target.value) || 0)}
                  className="flex-1 text-center h-full outline-none font-bold text-slate-800 text-lg [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  onClick={() => setCustomAmount(prev => prev + 50)}
                  className="w-14 h-full bg-slate-50 hover:bg-slate-100 border-l border-slate-200 flex items-center justify-center text-slate-600 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line text-lg"></i>
                </button>
              </div>
              <div className="mt-3 flex justify-between text-xs text-slate-400 px-1">
                <span>Min: 25 credits</span>
                <span className="font-semibold text-emerald-600">
                  Rate: ₹{getCustomRate(customAmount).toFixed(2)}/credit
                </span>
              </div>
            </div>

            {/* Calculations & Purchase Button */}
            <div className="w-full md:w-1/2 flex flex-col justify-between h-full text-left">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">TOTAL AMOUNT</span>
                <div className="flex items-baseline gap-2">
                  <h3 className="text-3xl font-black text-slate-800">
                    ₹{getCustomPrice(customAmount).toFixed(2)}
                  </h3>
                  <span className="text-xs text-slate-400">taxes included</span>
                </div>
              </div>

              <button
                onClick={() => handleBuy(null, customAmount, 'custom')}
                disabled={purchasingId !== null || customAmount < 25}
                className="w-full mt-6 py-3.5 px-4 bg-[#0859b8] hover:bg-[#06428b] text-white rounded-xl text-sm font-bold tracking-wide uppercase shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {purchasingId === 'custom' ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="ri-secure-payment-line"></i>
                    Pay ₹{getCustomPrice(customAmount).toFixed(2)}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Section 4: Pricing Footer Note */}
        <div className="bg-slate-100/70 rounded-2xl border border-slate-200/60 p-6 text-left">
          <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-1">
            <i className="ri-information-fill text-slate-400 text-sm"></i>
            Pay-As-You-Go Credits Rates
          </h4>
          <p className="text-xs text-slate-500 leading-relaxed mb-4">
            Credits are deducted only when generations succeed. If a generation fails, the spent credits are immediately refunded back to your balance. Below are the credit costs per action:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/80 p-3.5 rounded-xl border border-slate-200/50">
              <span className="text-[10px] font-bold text-slate-400 block uppercase">Video Post</span>
              <span className="text-sm font-bold text-slate-700">{CREDIT_COSTS.VIDEO_POST} credits</span>
            </div>
            <div className="bg-white/80 p-3.5 rounded-xl border border-slate-200/50">
              <span className="text-[10px] font-bold text-slate-400 block uppercase">Full Website</span>
              <span className="text-sm font-bold text-slate-700">{websiteBuilderCost.toLocaleString()} credits</span>
            </div>
            <div className="bg-white/80 p-3.5 rounded-xl border border-slate-200/50">
              <span className="text-[10px] font-bold text-slate-400 block uppercase">Social Post</span>
              <span className="text-sm font-bold text-slate-700">{CREDIT_COSTS.STATIC_POST} credits</span>
            </div>
            <div className="bg-white/80 p-3.5 rounded-xl border border-slate-200/50">
              <span className="text-[10px] font-bold text-slate-400 block uppercase">SEO Blog Article</span>
              <span className="text-sm font-bold text-slate-700">{CREDIT_COSTS.SEO_BLOG} credits</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
